import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../AuthProvider";
import { deleteCar, fetchCars, updateCarStatus } from "../services/carService";

const statusLabels = {
  pending: "Ausstehend",
  approved: "Freigegeben",
  rejected: "Abgelehnt",
};

const statusOptions = [
  { value: "all", label: "Alle Status" },
  { value: "pending", label: "Ausstehend" },
  { value: "approved", label: "Freigegeben" },
  { value: "rejected", label: "Abgelehnt" },
];

const fuelTypeLabels = {
  electric: "Elektrisch",
  ice: "Verbrenner",
  hybrid: "Hybrid",
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("de-CH", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const StatusBadge = ({ status }) => (
  <span className={`car-pill car-pill-${status || "pending"}`}>
    {statusLabels[status] ?? status ?? "Unbekannt"}
  </span>
);

const CarPhotoPreview = ({ hasPhoto, photoEndpoint, fetchWithAuth, label = "Ansehen" }) => {
  const [photoUrl, setPhotoUrl] = useState(null);
  const [loadingPhoto, setLoadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!hasPhoto || !photoEndpoint) {
      setPhotoUrl(null);
      setPhotoError(null);
      setLoadingPhoto(false);
      return;
    }

    let isActive = true;
    let objectUrl = null;
    setLoadingPhoto(true);
    setPhotoError(null);

    fetchWithAuth(photoEndpoint)
      .then((res) => {
        if (!res.ok) {
          throw new Error("Foto konnte nicht geladen werden.");
        }
        return res.blob();
      })
      .then((blob) => {
        if (!isActive) return;
        objectUrl = URL.createObjectURL(blob);
        setPhotoUrl(objectUrl);
      })
      .catch((err) => {
        if (!isActive) return;
        setPhotoError(err.message || "Foto konnte nicht geladen werden.");
        setPhotoUrl(null);
      })
      .finally(() => {
        if (isActive) {
          setLoadingPhoto(false);
        }
      });

    return () => {
      isActive = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [hasPhoto, photoEndpoint, fetchWithAuth, reloadKey]);

  const retry = () => setReloadKey((key) => key + 1);
  const handleOpenFullSize = () => {
    if (photoUrl) {
      window.open(photoUrl, "_blank", "noopener,noreferrer");
    }
  };

  if (!hasPhoto) {
    return <div className="car-photo-placeholder">Kein Foto</div>;
  }

  if (loadingPhoto) {
    return <div className="car-photo-placeholder">Lädt…</div>;
  }

  if (photoError) {
    return (
      <button type="button" className="car-photo-retry" onClick={retry}>
        Neu laden
      </button>
    );
  }

  return (
    <button type="button" className="car-photo-button" onClick={handleOpenFullSize}>
      {photoUrl ? <img src={photoUrl} alt="Verifizierungsfoto" /> : null}
      <span>{label}</span>
    </button>
  );
};

const CarPhotoGallery = ({ car, fetchWithAuth }) => {
  const photos = [
    { key: "front", label: "Front" },
    { key: "back", label: "Heck" },
    { key: "left", label: "Links" },
    { key: "right", label: "Rechts" },
    { key: "registration_document", label: "Fahrzeugausweis" },
  ];
  const photoSummary = car.verification_photos || {};
  const hasAnyTypedPhoto = photos.some((photo) => photoSummary[photo.key]?.has_photo);

  if (!hasAnyTypedPhoto) {
    return (
      <CarPhotoPreview
        hasPhoto={car.hasPhoto}
        photoEndpoint={car.photoEndpoint}
        fetchWithAuth={fetchWithAuth}
        label="Legacy"
      />
    );
  }

  return (
    <div className="car-photo-grid">
      {photos.map((photo) => (
        <CarPhotoPreview
          key={photo.key}
          hasPhoto={Boolean(photoSummary[photo.key]?.has_photo)}
          photoEndpoint={car.photoEndpoints?.[photo.key] || null}
          fetchWithAuth={fetchWithAuth}
          label={photo.label}
        />
      ))}
    </div>
  );
};

export default function FahrzeugePage() {
  const { fetchWithAuth } = useAuth();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [updatingId, setUpdatingId] = useState(null);

  const loadCars = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const results = await fetchCars(fetchWithAuth);
      setCars(results);
    } catch (err) {
      setError(err.message || "Fahrzeuge konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    loadCars();
  }, [loadCars]);

  const filteredCars = useMemo(() => {
    return cars.filter((car) => {
      const haystack = [
        car.driver_name,
        car.city,
        car.manufacturer,
        car.model,
        car.number_plate,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const matchesSearch = haystack.includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === "all" || car.validation_status === filterStatus;
      return matchesSearch && matchesStatus;
    });
  }, [cars, searchTerm, filterStatus]);

  const handleStatusChange = async (carId, nextStatus) => {
    setUpdatingId(carId);
    setError(null);
    try {
      const updated = await updateCarStatus(fetchWithAuth, carId, nextStatus);
      if (updated) {
        setCars((existing) => existing.map((car) => (car.car_id === carId ? updated : car)));
      } else {
        setCars((existing) =>
          existing.map((car) =>
            car.car_id === carId ? { ...car, validation_status: nextStatus } : car
          )
        );
      }
    } catch (err) {
      setError(err.message || "Status konnte nicht aktualisiert werden.");
    } finally {
      setUpdatingId(null);
    }
  };

  const requestStatusChange = (car, nextStatus) => {
    const actionLabel =
      nextStatus === "approved"
        ? "wirklich freigeben"
        : nextStatus === "rejected"
        ? "wirklich ablehnen"
        : "ändern";
    const detail = `${car.manufacturer ?? ""} ${car.model ?? ""}`.trim();
    const target = car.number_plate ? `${car.number_plate}${detail ? ` – ${detail}` : ""}` : detail;
    const message = `Möchtest du ${target || "dieses Fahrzeug"} ${actionLabel}?`;
    if (!window.confirm(message)) return;
    handleStatusChange(car.car_id, nextStatus);
  };

  const handleDeleteCar = async (car) => {
    const detail = `${car.manufacturer ?? ""} ${car.model ?? ""}`.trim();
    const target = car.number_plate ? `${car.number_plate}${detail ? ` – ${detail}` : ""}` : detail;
    if (!window.confirm(`Möchtest du ${target || "dieses Fahrzeug"} und alle zugehörigen Medien endgültig löschen?`)) {
      return;
    }
    setUpdatingId(car.car_id);
    setError(null);
    try {
      await deleteCar(fetchWithAuth, car.car_id);
      setCars((existing) => existing.filter((entry) => entry.car_id !== car.car_id));
    } catch (err) {
      setError(err.message || "Fahrzeug konnte nicht gelöscht werden.");
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="portal-section car-panel">
      <header className="car-top">
        <div>
          <p className="car-eyebrow">Fahrzeugprüfung</p>
          <h1 className="portal-title">Fahrzeuge</h1>
          <p className="portal-desc">
            Prüfe neue Registrierungen und entscheide, ob sie freigegeben werden sollen.
          </p>
        </div>
        <div className="car-top-actions">
          <input
            className="car-search"
            placeholder="Suche nach Fahrerin, Ort oder Nummernschild"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
          <select
            className="car-filter"
            value={filterStatus}
            onChange={(event) => setFilterStatus(event.target.value)}
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button className="car-refresh" onClick={loadCars} disabled={loading}>
            Neu laden
          </button>
        </div>
      </header>

      {error && <div className="car-alert error">{error}</div>}

      <div className="car-table">
        <div className="car-table-top">
          <span>Fotos</span>
          <span>Fahrerin</span>
          <span>Fahrzeug</span>
          <span>Nummernschild</span>
          <span>Status</span>
          <span>Anfrage</span>
          <span>Aktion</span>
        </div>

        {loading ? (
          <div className="car-empty">Fahrzeuge werden geladen…</div>
        ) : filteredCars.length === 0 ? (
          <div className="car-empty">Keine Fahrzeuge gefunden.</div>
        ) : (
          filteredCars.map((car) => (
            <div key={car.car_id} className="car-row">
              <div className="car-photo-cell">
                <CarPhotoGallery car={car} fetchWithAuth={fetchWithAuth} />
              </div>
              <div>
                <div className="car-strong">{car.driver_name ?? "Unbekannt"}</div>
                <div className="car-muted">{car.city ?? "—"}</div>
              </div>
              <div>
                <div className="car-strong">
                  {car.manufacturer} {car.model}
                </div>
                <div className="car-muted">
                  Antrieb: {fuelTypeLabels[car.fuel_type] ?? "—"}
                </div>
                <div className="car-muted">
                  Lack: {car.paint_code ?? "—"} ·{" "}
                  {typeof car.odometer === "number"
                    ? `${car.odometer.toLocaleString("de-CH")} km`
                    : "—"}
                </div>
              </div>
              <div className="car-strong">{car.number_plate ?? "—"}</div>
              <div>
                <StatusBadge status={car.validation_status} />
                {car.validation_summary && (
                  <div className="car-muted">{car.validation_summary}</div>
                )}
              </div>
              <div>
                <div className="car-muted">Angefragt</div>
                <div className="car-strong">{formatDate(car.validation_requested_at)}</div>
                {car.validation_completed_at && (
                  <div className="car-muted">
                    Genehmigt: {formatDate(car.validation_completed_at)}
                  </div>
                )}
              </div>
              <div className="car-actions">
                <button
                  className="btn btn-approve"
                  disabled={updatingId === car.car_id || car.validation_status === "approved"}
                  onClick={() => requestStatusChange(car, "approved")}
                >
                  Freigeben
                </button>
                <button
                  className="btn btn-reject"
                  disabled={updatingId === car.car_id || car.validation_status === "rejected"}
                  onClick={() => requestStatusChange(car, "rejected")}
                >
                  Ablehnen
                </button>
                <button
                  className="btn btn-reject"
                  disabled={updatingId === car.car_id}
                  onClick={() => handleDeleteCar(car)}
                >
                  Löschen
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
